import { Draggable, type DragOptions } from '@neodrag/core';
import { untrack } from 'svelte';
import type { Attachment } from 'svelte/attachments';

export type { DragEventData, DragOptions } from '@neodrag/core';

// neodrag v3 as a Svelte attachment: `{@attach draggable(() => ({ ...options }))}`.
// The options thunk is re-read reactively and pushed to the instance with `update()`, so a
// controlled `position` or `disabled` can follow reactive state (e.g. a tile's board coords).
// @neodrag/svelte's `Draggable` class is built for one element per instance, which doesn't
// fit tiles rendered in an `{#each}`.
export const draggable =
  (options: () => DragOptions): Attachment<HTMLElement> =>
  (node) => {
    // v3 ignores `position` updates while dragging, so when a drag ends, snap to the current
    // controlled position even if it didn't change. Otherwise a tile whose state was reset
    // mid-drag stays wherever it was dropped.
    const withSnap = (o: DragOptions): DragOptions => ({
      ...o,
      onDragEnd: (e) => {
        o.onDragEnd?.(e);
        const { position } = untrack(options);
        if (position) instance.update({ position });
      },
    });
    const instance = new Draggable(node, withSnap(untrack(options)));
    $effect(() => {
      instance.update(withSnap(options()));
    });
    return () => instance.destroy();
  };
